import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input, OnChanges,
  OnDestroy,
  OnInit, SimpleChanges,
  ViewChild
} from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective, FormsModule,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms'
import {TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {NgxCurrencyConfig} from "ngx-currency/lib/ngx-currency.config";
import {CoreModule} from "../../../../core.module";
import {NgxCurrencyDirective} from "ngx-currency";
import {TooltipModule} from "ngx-bootstrap/tooltip";



@Component({
    selector: 'app-currency-input',
    templateUrl: './currency-input.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: CurrencyInputComponent,
            multi: true
        }],
    imports: [CoreModule, FormsModule, NgxCurrencyDirective, TooltipModule],
    standalone: true
})
export class CurrencyInputComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {

  @Input() public label!: string
  @Input() public small = false

  @Input() public errorMessages!: Record<string, string>
  @Input() public name!: string
  @Input() public prefix?: string
  /**
   * das select wird immer eine array von values ausgeben (bei multi === true)
   * order EIN value bei  !multi
   */

  @Input() public tooltipText?: string

  @Input() public autofocus = false
  @Input() public inputCounterMax: number | null = null
  @Input() public placeholder = ''

  @Input() public allowNegative = false;
  @Input() public value?: number; // Value in cents
  @Input() public disabled = false;

  public inputValue = '';
  private internalFormControl?: FormControl;

  public currencyOptions: Partial<NgxCurrencyConfig> = {
    allowNegative: this.allowNegative,
    allowZero: true,
    precision: 2,
    prefix: '',
    suffix: ' €',
    nullable: false,
    inputMode: 0, // FINANCIAL
  };

  private $controlValueChanges?: Subscription;
  private $translationChanges?: Subscription;

  public changed(event: string | number) {
    this.control.setValue(+event * 100);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.cdr.markForCheck();
  }

  public ngOnInit() {
    this.setCurrencyOptionsDecimalAndThousandsSeparators();

    // If value input is provided, create an internal FormControl
    if (this.value !== undefined) {
      this.internalFormControl = new FormControl({value: this.value, disabled: this.disabled});
    }

    this.fillNgModel();

    this.$controlValueChanges = this.control.valueChanges.subscribe({
      next: () => {
        this.fillNgModel();
      }
    })

    this.$translationChanges = this.translate.onLangChange.subscribe(() => {
      this.setCurrencyOptionsDecimalAndThousandsSeparators();
      this.cdr.markForCheck();
    })
  }

  public ngOnDestroy() {
    this.$controlValueChanges?.unsubscribe();
    this.$translationChanges?.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if('allowNegative' in changes && changes['allowNegative'].previousValue !== changes['allowNegative'].currentValue) {
      this.currencyOptions = {...this.currencyOptions, allowNegative: changes['allowNegative'].currentValue};
      this.cdr.markForCheck();
    }

    // Handle value changes
    if('value' in changes && this.internalFormControl) {
      this.internalFormControl.setValue(changes['value'].currentValue, {emitEvent: false});
      this.fillNgModel();
    }

    // Handle disabled changes
    if('disabled' in changes && this.internalFormControl) {
      if (changes['disabled'].currentValue) {
        this.internalFormControl.disable({emitEvent: false});
      } else {
        this.internalFormControl.enable({emitEvent: false});
      }
    }
  }

  private fillNgModel() {
    const controlValue = this.control.getRawValue();
    this.inputValue = controlValue / 100 + '';
    this.cdr.markForCheck();
  }

  public requiredValidator = Validators.required

  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl?: FormControl;

  @Input()
  public formControlName!: string;

  public get control(): FormControl {
    // If using value input, return internal FormControl
    if (this.internalFormControl) {
      return this.internalFormControl;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <FormControl>this.formControl ?? this.controlContainer.control.get(this.formControlName);
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(obj: () => void): void {
    this.formControlDirective?.valueAccessor?.writeValue(obj);
  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  private setCurrencyOptionsDecimalAndThousandsSeparators() {
    this.currencyOptions.decimal = this.translate.currentLang === 'de' ? ',' : '.';
    this.currencyOptions.thousands = this.translate.currentLang === 'de' ? '.' : ',';
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector,
    private readonly translate: TranslateService,
  ) {
  }


}
